import React, { Fragment, useState, useCallback, MouseEvent } from "react";
import { Notice, openIntercom, Flex, OptionCard, Text, Button } from "@thenounproject/lingo-core";
import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalFooter from "../../../../app/_components/ModalFooter";
import ModalBody from "../../../../app/_components/ModalBody";
import { useSelectSpace } from "@selectors/entities/spaces";
import useApplyProductTypeTemplate from "@actions/productTypes/useApplyProductTypeTemplate";

type Props = {
  dismissModal?: (e?: MouseEvent | KeyboardEvent) => void;
  initialName?: string;
  isEdit?: boolean;
  marketId?: string;
};

type Industry = {
  id: string;
  name: string;
  description: string;
  types: string[];
};
const INDUSTRIES: Industry[] = [
  {
    id: "cannabis",
    name: "Cannabis",
    description:
      "Product types for typical cannaabis products such as flower, vapes, tinctures and more.",
    types: ["Flower", "Pre-Roll", "Vaporizer", "Concentrate", "Edible", "Topical", "Tincture"],
  },
];

const ProductTypePresetsModal: React.FC<Props> = ({ dismissModal }) => {
  const [selectedIndustry, setSelectedIndustry] = useState<Industry>(null);
  const space = useSelectSpace();

  const [applyTemplate, { isProcessing }] = useApplyProductTypeTemplate();

  const contactUs = useCallback(() => {
    openIntercom({
      launchChat: true,
      message: "I am interested in setting up product types for my industry",
    });
  }, []);

  const onSubmit = useCallback(async () => {
    const res = await applyTemplate({ spaceId: space.id, templateKey: "cannabis_types" });
    if (!res.error) {
      dismissModal();
    }
  }, [applyTemplate, dismissModal, space.id]);

  function renderIndustryList() {
    return (
      <ModalBody>
        <Flex flexDirection="column" gap="8px">
          {INDUSTRIES.map(industry => (
            <OptionCard
              key={industry.id}
              title={industry.name}
              detail={industry.description}
              onClick={() => setSelectedIndustry(industry)}
              cardStyle="disclosure"
            />
          ))}
        </Flex>
        <Notice
          noticeStyle="neutral"
          message="Don’t see your industry? Contact us to learn more about setting up product types for your products."
          button={{ text: "Contact us", onClick: contactUs }}
        />
      </ModalBody>
    );
  }

  function renderIndustry() {
    return (
      <>
        <ModalBody alignItems="flex-start">
          <Button
            buttonStyle="tertiary"
            text="Back"
            iconBefore="navigation.chevron-left"
            size="small"
            mb="l"
            onClick={() => setSelectedIndustry(null)}
          />
          <Text font="ui.title">{selectedIndustry.name}</Text>
          <Text font="ui.regular" mb="m">
            {selectedIndustry.description}
          </Text>
          <Flex flexDirection="column" as="ul">
            {selectedIndustry.types.map(t => (
              <Text key={t} as="li" style={{ listStyle: "disc", listStylePosition: "inside" }}>
                {t}
              </Text>
            ))}
          </Flex>
        </ModalBody>
        <ModalFooter
          primary={{
            text: isProcessing ? "Saving..." : "Create Product Types",
            onClick: onSubmit,
            disabled: isProcessing,
          }}
        />
      </>
    );
  }

  return (
    <>
      <ModalHeader title={"Product Type Presets"} />
      {selectedIndustry ? renderIndustry() : renderIndustryList()}
    </>
  );
};

export default ProductTypePresetsModal;
